/**
 * Date Utilities for Business Day Calculations
 * 
 * Provides functions to calculate business days considering:
 * - Weekends (Saturday/Sunday)
 * - Chilean public holidays
 * - Chilean timezone (America/Santiago)
 */

import { startOfISOWeek, endOfISOWeek, addDays, format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { isChileanHoliday } from './chileanHolidays';

/**
 * Chilean timezone constant
 */
const CHILE_TIMEZONE = 'America/Santiago';

/**
 * Check if a date falls on a weekend (Saturday or Sunday)
 * 
 * @param {Date} date - The date to check
 * @returns {boolean} True if weekend, false otherwise
 */
export const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
};

/**
 * Check if a date is a business day (not weekend and not holiday)
 * 
 * @param {Date} date - The date to check
 * @returns {boolean} True if business day, false otherwise
 */
export const isBusinessDay = (date) => {
  return !isWeekend(date) && !isChileanHoliday(date);
};

/**
 * Get the first business day of an ISO week
 * Skips weekends and Chilean public holidays
 * 
 * @param {number} year - The year (e.g., 2026)
 * @param {number} week - The ISO week number (1-53)
 * @returns {string} Date in YYYY-MM-DD format (first business day of the week)
 */
export const getFirstBusinessDayOfWeek = (year, week) => {
  // Get the start of the ISO week (Monday)
  const weekStart = startOfISOWeek(new Date(year, 0, 1 + (week - 1) * 7));
  
  // Convert to Chilean timezone
  const weekStartChile = toZonedTime(weekStart, CHILE_TIMEZONE);
  
  // Find the first business day
  let currentDate = weekStartChile;
  let daysChecked = 0;
  const MAX_DAYS_TO_CHECK = 14; // Safety limit (2 weeks)
  
  while (daysChecked < MAX_DAYS_TO_CHECK) {
    if (isBusinessDay(currentDate)) {
      // Return in YYYY-MM-DD format
      return format(currentDate, 'yyyy-MM-dd');
    }
    
    // Move to next day
    currentDate = addDays(currentDate, 1);
    daysChecked++;
  }
  
  // Fallback: if no business day found in 2 weeks, return the Monday
  console.warn(`No business day found for week ${week} of ${year}, returning Monday`);
  return format(weekStartChile, 'yyyy-MM-dd');
};

/**
 * Format a date string to DD-MM-YYYY for display
 * 
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {string} Date in DD-MM-YYYY format
 */
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'No especificada';
  
  try {
    const date = parseISO(dateString);
    return format(date, 'dd-MM-yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

/**
 * Get current date in YYYY-MM-DD format for Chilean timezone
 * 
 * @returns {string} Current date in YYYY-MM-DD format
 */
export const getCurrentDateChile = () => {
  const now = new Date();
  const chileDate = toZonedTime(now, CHILE_TIMEZONE);
  return format(chileDate, 'yyyy-MM-dd');
};

/**
 * Get the start and end dates of an ISO week
 * 
 * @param {number} year - The year (e.g., 2026)
 * @param {number} week - The ISO week number (1-53)
 * @returns {{start: Date, end: Date}} Object with start (Monday) and end (Sunday) dates
 */
export const getWeekDateRange = (year, week) => {
  // Calculate the date for the first day of the given ISO week
  const weekStart = startOfISOWeek(new Date(year, 0, 1 + (week - 1) * 7));
  const weekEnd = endOfISOWeek(weekStart);
  
  return {
    start: weekStart,
    end: weekEnd
  };
};

/**
 * Check if a date falls within a specific ISO week
 * 
 * @param {Date} date - The date to check
 * @param {number} year - The year (e.g., 2026)
 * @param {number} week - The ISO week number (1-53)
 * @returns {boolean} True if date is in the specified week, false otherwise
 */
export const isDateInWeek = (date, year, week) => {
  if (!date || !year || !week) return false;
  
  const { start, end } = getWeekDateRange(year, week);
  const checkDate = new Date(date);
  
  return checkDate >= start && checkDate <= end;
};

/**
 * Get all 7 dates in an ISO week (Monday through Sunday)
 * 
 * @param {number} year - The year (e.g., 2026)
 * @param {number} week - The ISO week number (1-53)
 * @returns {Date[]} Array of 7 Date objects representing the week
 */
export const getWeekDates = (year, week) => {
  const { start } = getWeekDateRange(year, week);
  const dates = [];
  
  for (let i = 0; i < 7; i++) {
    dates.push(addDays(start, i));
  }
  
  return dates;
};
